import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  meta?: boolean; // Cmd on Mac, Windows key on PC
  description: string;
  action: () => void;
  global?: boolean; // Whether this shortcut works everywhere
  allowInInput?: boolean; // Whether to allow when focused on input/textarea
}

// Define all keyboard shortcuts
const getShortcuts = (
  navigate: ReturnType<typeof useNavigate>,
  location: ReturnType<typeof useLocation>,
  logout: () => void,
  setShowHelp: (show: boolean) => void,
  setShowSearch: (show: boolean) => void
): ShortcutConfig[] => [
  // Navigation shortcuts
  {
    key: 'd',
    meta: true,
    description: 'Go to Dashboard',
    action: () => navigate('/process'),
    global: true,
  },
  {
    key: 'p',
    meta: true,
    description: 'Process Documents',
    action: () => navigate('/process'),
    global: true,
  },
  {
    key: 't',
    meta: true,
    description: 'Transaction History',
    action: () => navigate('/transactions'),
    global: true,
  },
  {
    key: 'c',
    meta: true,
    description: 'Compliance Dashboard',
    action: () => navigate('/compliance'),
    global: true,
  },
  {
    key: 'm',
    meta: true,
    description: 'Magic Marker',
    action: () => navigate('/magic-marker'),
    global: true,
  },
  {
    key: 'a',
    meta: true,
    shift: true,
    description: 'Admin Dashboard',
    action: () => navigate('/admin'),
    global: true,
  },

  // Action shortcuts
  {
    key: 'k',
    meta: true,
    description: 'Open Quick Search',
    action: () => setShowSearch(true),
    global: true,
  },
  {
    key: '/',
    description: 'Focus Search',
    action: () => {
      const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search"]') as HTMLInputElement;
      searchInput?.focus();
    },
    global: true,
    allowInInput: false,
  },
  {
    key: 'n',
    meta: true,
    description: 'New Document',
    action: () => {
      navigate('/process');
      // Trigger file upload after navigation
      setTimeout(() => {
        const uploadButton = document.querySelector('input[type="file"]') as HTMLInputElement;
        uploadButton?.click();
      }, 100);
    },
    global: true,
  },
  {
    key: 'Escape',
    description: 'Close Modal/Dialog',
    action: () => {
      // Close any open modals
      const closeButtons = document.querySelectorAll('[aria-label="close"], .MuiModal-root button[aria-label*="Close"]');
      const lastButton = closeButtons[closeButtons.length - 1] as HTMLElement;
      lastButton?.click();
    },
    global: true,
    allowInInput: true,
  },
  {
    key: 's',
    meta: true,
    description: 'Save Current Work',
    action: () => {
      // Trigger save on current page
      const saveButton = document.querySelector('button[type="submit"], button:contains("Save")') as HTMLElement;
      saveButton?.click();
    },
    global: false,
  },
  {
    key: 'Enter',
    meta: true,
    description: 'Submit Form',
    action: () => {
      const submitButton = document.querySelector('button[type="submit"]') as HTMLElement;
      submitButton?.click();
    },
    global: false,
    allowInInput: true,
  },

  // View shortcuts
  {
    key: '1',
    alt: true,
    description: 'View Mode: List',
    action: () => {
      const listViewButton = document.querySelector('[data-view="list"]') as HTMLElement;
      listViewButton?.click();
    },
    global: false,
  },
  {
    key: '2',
    alt: true,
    description: 'View Mode: Grid',
    action: () => {
      const gridViewButton = document.querySelector('[data-view="grid"]') as HTMLElement;
      gridViewButton?.click();
    },
    global: false,
  },
  {
    key: 'r',
    meta: true,
    description: 'Refresh Data',
    action: () => {
      window.location.reload();
    },
    global: true,
  },

  // Help & Support
  {
    key: '?',
    shift: true,
    description: 'Show Keyboard Shortcuts',
    action: () => setShowHelp(true),
    global: true,
    allowInInput: false,
  },
  {
    key: 'h',
    meta: true,
    description: 'Show Help',
    action: () => setShowHelp(true),
    global: true,
  },

  // User actions
  {
    key: 'l',
    meta: true,
    shift: true,
    description: 'Logout',
    action: () => {
      if (window.confirm('Are you sure you want to logout?')) {
        logout();
      }
    },
    global: true,
  },
  {
    key: 'u',
    meta: true,
    description: 'User Profile',
    action: () => navigate('/profile'),
    global: true,
  },
];

export const useKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthStore();
  const [showHelp, setShowHelp] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [recentKeys, setRecentKeys] = useState<string[]>([]);

  // Get shortcuts
  const shortcuts = getShortcuts(navigate, location, logout, setShowHelp, setShowSearch);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Check if user is typing in an input field
    const target = event.target as HTMLElement;
    const isInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
                        target.contentEditable === 'true';

    // Track recent keys for combo detection (like vim-style commands)
    setRecentKeys(prev => {
      const newKeys = [...prev, event.key].slice(-3);

      // Check for special combos (e.g., 'gg' for go to top)
      if (newKeys.join('') === 'gg') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return [];
      }
      if (newKeys.join('') === 'GG') {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        return [];
      }

      return newKeys;
    });

    // Find matching shortcut
    const matchingShortcut = shortcuts.find(shortcut => {
      // Check key match
      if (shortcut.key.toLowerCase() !== event.key.toLowerCase()) {
        return false;
      }

      // Check modifiers
      if (shortcut.ctrl !== undefined && shortcut.ctrl !== event.ctrlKey) return false;
      if (shortcut.alt !== undefined && shortcut.alt !== event.altKey) return false;
      if (shortcut.shift !== undefined && shortcut.shift !== event.shiftKey) return false;
      if (shortcut.meta !== undefined && shortcut.meta !== (event.metaKey || event.ctrlKey)) return false;

      // Check if allowed in input fields
      if (isInputField && !shortcut.allowInInput) {
        return false;
      }

      // Check if global or on correct page
      if (!shortcut.global) {
        // Page-specific shortcuts - implement page detection logic here
        return true;
      }

      return true;
    });

    if (matchingShortcut) {
      event.preventDefault();
      event.stopPropagation();
      matchingShortcut.action();
    }
  }, [shortcuts]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    shortcuts,
    showHelp,
    setShowHelp,
    showSearch,
    setShowSearch,
  };
};

// Format shortcut for display - accepts partial config for flexibility
export const formatShortcut = (shortcut: Pick<ShortcutConfig, 'key' | 'ctrl' | 'alt' | 'shift' | 'meta'>): string => {
  const parts: string[] = [];

  if (shortcut.meta) {
    // Show Cmd for Mac, Ctrl for Windows/Linux
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.ctrl) parts.push('Ctrl');
  if (shortcut.alt) parts.push('Alt');
  if (shortcut.shift) parts.push('Shift');

  // Format the key
  let key = shortcut.key;
  if (key === 'Escape') key = 'Esc';
  if (key === 'Enter') key = '↵';
  if (key === ' ') key = 'Space';

  parts.push(key.toUpperCase());

  return parts.join('+');
};

export default useKeyboardShortcuts;